import React, { useState } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { useNotifications } from 'modules/app/notifications';
import { type AnalyticalFieldExportSettings } from 'src/core/modules/accounting-integration/apis/useAnalyticalFieldExportSettings';
import { type SyncMode } from 'src/core/modules/accounting-integration/apis/useSetSyncSettings';
import { useAnalyticalFieldMapping } from 'src/core/modules/accounting-integration/hooks/useAnalyticalFieldMapping';
import { useHasMapSync } from 'src/core/modules/accounting-integration/hooks/useHasMapSync';
import {
  type AnalyticalFieldValueMappingV2,
  type AnalyticalFieldMappingV2,
} from 'src/core/modules/bookkeep';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

import { useCreateCustomFieldMutation } from './useCreateCustomFieldMutation';
import { CustomFieldCreationModal } from '../../components/CustomFieldCreationModal';
import { type SyncErrors } from '../../components/CustomFieldCreationModal/CustomFieldCreationModal';
import { convertValues } from '../../utils/convertValues';
import { removeDuplicates } from '../../utils/removeDuplicates';
import { useDefaultCustomFieldPayload } from '../customField';

type Props = {
  isOpen: boolean;
  onClose(): void;
};

export type CustomFieldCreationForm = {
  name: string;
  type: 'optional' | 'mandatory';
  isSplittable?: boolean;
  values?: string;
};

export const CustomFieldCreationModalContainer = ({
  isOpen,
  onClose,
}: Props) => {
  const [createCustomField] = useCreateCustomFieldMutation();
  const { t } = useTranslation('global');
  const { successNotif, dangerNotif } = useNotifications();

  const isMapSyncEnabled = useHasMapSync();

  const defaultCustomFieldPayload = useDefaultCustomFieldPayload();

  const [syncMode, setSyncMode] = useState<SyncMode | undefined>(undefined);
  const [exportSettings, setExportSettings] = useState<
    AnalyticalFieldExportSettings | undefined
  >({
    externalId: '',
    sendOnLine: true,
    sendOnHeader: false,
  });
  const [error, setError] = useState<SyncErrors | null>(null);

  const [valueMapping, setValueMapping] =
    useState<AnalyticalFieldValueMappingV2>();

  const {
    analyticalFieldMappings,
    fieldMapping,
    setFieldMapping,
    onSaveChanges,
  } = useAnalyticalFieldMapping({
    fieldType: 'customField',
  });

  const handleCustomFieldCreation = async (values: CustomFieldCreationForm) => {
    try {
      const data = await createCustomField({
        ...defaultCustomFieldPayload,
        name: values.name,
        isRequired: values.type === 'mandatory',
        values: removeDuplicates(convertValues(values.values)),
        isSplittable: values.isSplittable,
      });
      successNotif(t('customFields.creation.successRequest'));

      if (!isMapSyncEnabled || syncMode !== 'pull') {
        onClose();
      }

      track(AnalyticEventName.CUSTOM_FIELDS_FORM_POST_VALUES, {
        method: 'creation',
        numberOfCharacters: (values.values ?? '').length,
        numberOfValues: (values.values ?? '').split('\n').length,
      });
      return data;
    } catch {
      dangerNotif(t('customFields.creation.errorRequest'));
    }
  };

  const handleSubmit = async (values: CustomFieldCreationForm) => {
    if (isMapSyncEnabled && syncMode === 'pull' && !fieldMapping) {
      setError('missingSelectedField');
      return;
    }

    const customFieldResult = await handleCustomFieldCreation(values);
    const customFieldId = customFieldResult?.result.createdCustomFieldId;

    if (!isMapSyncEnabled || !customFieldId || syncMode !== 'pull') {
      return;
    }

    const mapping = {
      // TS does not infer correctly here
      fieldId: (fieldMapping as unknown as AnalyticalFieldMappingV2).id,
      fieldType: 'customField' as const,
      internalId: customFieldId,
    };

    onSaveChanges({
      mapping,
      syncMode: 'pull',
      exportSettings,
    });
  };

  return (
    <CustomFieldCreationModal
      isOpen={isOpen}
      isMapSyncEnabled={isMapSyncEnabled}
      syncMode={syncMode}
      setSyncMode={setSyncMode}
      error={error}
      setError={setError}
      onClose={onClose}
      onSubmit={handleSubmit}
      analyticalFieldMappings={analyticalFieldMappings}
      fieldMapping={fieldMapping}
      setFieldMapping={setFieldMapping}
      exportSettings={exportSettings}
      setExportSettings={setExportSettings}
      valueMapping={valueMapping}
      setValueMapping={setValueMapping}
    />
  );
};
